import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ScanLine, Check, ArrowLeft, X } from 'lucide-react'

const MedicalRegister = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')

  // 模拟已分配账号的医护数据（后台创建后待激活）
  const mockPendingAccounts = [
    { phone: '180****5555', name: '刘医生', hospital: '彭山区疾控中心', role: 'doctor' },
    { phone: '180****6666', name: '陈护士', hospital: '仁寿县人民医院', role: 'nurse' },
  ]

  const sendCode = () => {
    if (!phone) {
      setError('请输入手机号')
      return
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的手机号')
      return
    }
    setError('')
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleNext = () => {
    setError('')
    if (step === 1) {
      if (!phone || !code) {
        setError('请填写手机号和验证码')
        return
      }
      // 模拟验证手机号是否在待激活列表中
      setStep(2)
    } else if (step === 2) {
      if (!password || !confirmPassword) {
        setError('请填写密码')
        return
      }
      if (password.length < 6) {
        setError('密码至少6位')
        return
      }
      if (password !== confirmPassword) {
        setError('两次密码不一致')
        return
      }
      setStep(3)
    } else if (step === 3) {
      setLoading(true)
      setTimeout(() => {
        setLoading(false)
        navigate('/medical/dashboard')
      }, 1500)
    }
  }

  const getPasswordStrength = (pwd: string) => {
    let strength = 0
    if (pwd.length >= 6) strength++
    if (/[a-zA-Z]/.test(pwd)) strength++
    if (/\d/.test(pwd)) strength++
    if (/[!@#$%^&*]/.test(pwd)) strength++
    return strength
  }

  const strengthLabels = ['太弱', '较弱', '一般', '较强', '很强']
  const strengthColors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400', 'bg-green-600']

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 顶部导航 */}
      <div className="bg-green-700 text-white px-4 py-4 flex items-center">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}
        <h1 className="flex-1 text-center text-lg font-bold pr-8">
          {step === 1 ? '验证手机' : step === 2 ? '设置密码' : '激活成功'}
        </h1>
      </div>

      {/* 步骤指示 */}
      <div className="bg-white px-6 py-4">
        <div className="flex items-center justify-center space-x-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                  s < step
                    ? 'bg-green-600 text-white'
                    : s === step
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 mx-2 ${s < step ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-center space-x-8 mt-2 text-xs">
          <span className={step >= 1 ? 'text-green-600' : 'text-gray-400'}>验证手机</span>
          <span className={step >= 2 ? 'text-green-600' : 'text-gray-400'}>设置密码</span>
          <span className={step >= 3 ? 'text-green-600' : 'text-gray-400'}>完成</span>
        </div>
      </div>

      {/* 表单内容 */}
      <div className="flex-1 px-6 py-6">
        {/* 错误提示 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center">
            <X className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-red-600 text-sm">{error}</span>
          </div>
        )}

        {/* 步骤1: 手机验证 */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-green-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-green-700">
                <strong>账号激活</strong>：您的账号已由管理员创建，请先验证手机号完成激活。
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                手机号
              </label>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                验证码
              </label>
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="请输入验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={sendCode}
                  disabled={countdown > 0}
                  className={`px-4 py-4 rounded-xl font-medium whitespace-nowrap ${
                    countdown > 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-700 text-white hover:bg-green-800'
                  }`}
                >
                  {countdown > 0 ? `${countdown}秒` : '获取验证码'}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <p className="text-xs text-gray-500">
                提示：验证码为模拟数据任意填写即可演示。
              </p>
            </div>
          </div>
        )}

        {/* 步骤2: 设置密码 */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-green-700">
                验证通过，请为账号 <strong>{phone}</strong> 设置登录密码。
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                设置密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请设置登录密码（6-20位）"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-4 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* 密码强度 */}
            {password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full ${
                        i <= getPasswordStrength(password) ? strengthColors[getPasswordStrength(password) - 1] : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-xs ${
                  getPasswordStrength(password) <= 1 ? 'text-red-500' :
                  getPasswordStrength(password) <= 2 ? 'text-orange-500' :
                  getPasswordStrength(password) <= 3 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  密码强度：{strengthLabels[getPasswordStrength(password) - 1] || '太弱'}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                确认密码
              </label>
              <input
                type="password"
                placeholder="请再次输入密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <p className="text-sm text-gray-600 mb-2">密码要求:</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${password.length >= 6 ? 'text-green-600' : 'text-gray-300'}`} />
                  6-20位字符
                </li>
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${/[a-zA-Z]/.test(password) ? 'text-green-600' : 'text-gray-300'}`} />
                  包含字母
                </li>
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${/\d/.test(password) ? 'text-green-600' : 'text-gray-300'}`} />
                  包含数字
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* 步骤3: 成功 */}
        {step === 3 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">激活成功！</h2>
            <p className="text-gray-500 mb-8">您的账号已激活，现在可以使用手机号和密码登录了。</p>
            <div className="bg-gray-50 rounded-xl p-4 text-left">
              <p className="text-sm text-gray-600 mb-2">账号信息：</p>
              <p className="text-sm text-gray-700">手机号：{phone}</p>
              <p className="text-xs text-gray-400 mt-1">请妥善保管密码，不要泄露给他人</p>
            </div>
          </div>
        )}

        {/* 按钮 */}
        <button
          onClick={step === 3 ? () => navigate('/medical/login') : handleNext}
          disabled={loading}
          className={`w-full py-4 rounded-xl font-medium mt-6 transition-colors ${
            loading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-700 text-white hover:bg-green-800'
          }`}
        >
          {loading ? '处理中...' : step === 3 ? '去登录' : '下一步'}
        </button>

        {/* 返回登录 */}
        {step === 1 && (
          <p className="text-center mt-6 text-gray-600">
            已有账号?
            <Link to="/medical/login" className="text-green-600 font-medium ml-1">
              直接登录
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}

export default MedicalRegister
